// Colour picker (DRAW_PLAN §1 "Colour"): saturation/value square + hue, hex, RGB 0–255, and CMYK 0–100 in
// CMYK documents (CMYK values are stored exactly as typed, G10). `onChange(c, final)`: final = end of a drag.
import { useRef } from 'react';
import { useT } from '../../i18n';
import type { ColorRef } from '../model/types';
import { resolveColor } from '../render/paint';

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
export function rgbToHsv([r, g, b]: [number, number, number]): [number, number, number] {
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b),
    d = max - min;
  let h = 0;
  if (d) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
  }
  return [((h * 60 + 360) % 360) / 360, max ? d / max : 0, max];
}
export function hsvToRgb(h: number, s: number, v: number): [number, number, number] {
  const f = (n: number) => {
    const k = (n + h * 6) % 6;
    return v - v * s * Math.max(0, Math.min(k, 4 - k, 1));
  };
  return [f(5), f(3), f(1)];
}
export const toHex = ([r, g, b]: [number, number, number]) =>
  '#' +
  [r, g, b]
    .map((v) =>
      Math.round(clamp01(v) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('');
export function fromHex(s: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s.trim());
  if (!m) return null;
  const h = m[1]!.length === 3 ? [...m[1]!].map((c) => c + c).join('') : m[1]!;
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255) as [number, number, number];
}

export function ColorPicker({
  color,
  cmyk,
  onChange,
  testid = 'picker',
}: {
  color: ColorRef;
  cmyk: boolean;
  onChange: (c: ColorRef, final: boolean) => void;
  testid?: string;
}) {
  const t = useT();
  const rgb = resolveColor(color).rgb;
  const [h, s, v] = rgbToHsv(rgb);
  const sq = useRef<HTMLDivElement>(null);
  const setRgb = (c: [number, number, number], final: boolean) =>
    onChange({ rgb: c, alpha: color.alpha }, final);
  const pickSV = (e: React.PointerEvent, final: boolean) => {
    const r = sq.current!.getBoundingClientRect();
    setRgb(
      hsvToRgb(h, clamp01((e.clientX - r.left) / r.width), 1 - clamp01((e.clientY - r.top) / r.height)),
      final,
    );
  };
  const k4 = color.cmyk ?? [0, 0, 0, 0];
  return (
    <div data-testid={testid} style={{ display: 'grid', gap: 6, fontSize: 12 }}>
      <div
        ref={sq}
        data-testid={`${testid}-sv`}
        onPointerDown={(e) => {
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
          pickSV(e, false);
        }}
        onPointerMove={(e) => e.buttons && pickSV(e, false)}
        onPointerUp={(e) => pickSV(e, true)}
        style={{
          position: 'relative',
          height: 110,
          borderRadius: 3,
          background: `linear-gradient(to top, var(--pure-black), transparent), linear-gradient(to right, var(--pure-white), ${toHex(hsvToRgb(h, 1, 1))})`,
          touchAction: 'none',
          cursor: 'crosshair',
        }}
      >
        <span
          style={{
            position: 'absolute',
            left: `calc(${s * 100}% - 5px)`,
            top: `calc(${(1 - v) * 100}% - 5px)`,
            width: 10,
            height: 10,
            borderRadius: 5,
            border: '2px solid var(--pure-white)',
            boxShadow: '0 0 2px var(--pure-black)',
          }}
        />
      </div>
      <input
        type="range"
        aria-label={t('hue')}
        min={0}
        max={359}
        value={Math.round(h * 360)}
        onChange={(e) => setRgb(hsvToRgb(Number(e.target.value) / 360, s || 1, v || 1), false)}
        onPointerUp={() => setRgb(rgb, true)}
        style={{
          width: '100%',
          background: 'linear-gradient(to right, red, yellow, lime, cyan, blue, magenta, red)',
        }}
      />
      <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {t('hex')}
        <input
          data-testid={`${testid}-hex`}
          key={toHex(rgb)}
          defaultValue={toHex(rgb)}
          style={{ width: 80 }}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          onBlur={(e) => {
            const c = fromHex(e.target.value);
            if (c && toHex(c) !== toHex(rgb)) setRgb(c, true);
          }}
        />
      </label>
      <div style={{ display: 'flex', gap: 4 }}>
        {(['R', 'G', 'B'] as const).map((ch, i) => (
          <label key={ch} style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {ch}
            <input
              type="number"
              min={0}
              max={255}
              data-testid={`${testid}-${ch.toLowerCase()}`}
              key={`${ch}:${Math.round(rgb[i]! * 255)}`}
              defaultValue={Math.round(rgb[i]! * 255)}
              style={{ width: 46 }}
              onBlur={(e) => {
                const c = [...rgb] as [number, number, number];
                c[i] = clamp01(Number(e.target.value) / 255);
                setRgb(c, true);
              }}
            />
          </label>
        ))}
      </div>
      {cmyk && (
        <div style={{ display: 'flex', gap: 4 }} data-testid={`${testid}-cmyk`}>
          {(['C', 'M', 'Y', 'K'] as const).map((ch, i) => (
            <label key={ch} style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {ch}
              <input
                type="number"
                min={0}
                max={100}
                step="any"
                data-testid={`${testid}-${ch.toLowerCase()}`}
                key={`${ch}:${k4[i]}`}
                defaultValue={+(k4[i]! * 100).toFixed(2)}
                style={{ width: 46 }}
                onBlur={(e) => {
                  const c = [...k4] as [number, number, number, number];
                  c[i] = clamp01(Number(e.target.value) / 100);
                  onChange({ cmyk: c, alpha: color.alpha }, true);
                }}
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
